<script>
import { stats } from '@site/data/homeContent'
import AppIcon from '@site/components/common/AppIcon.vue'

export default {
  name: 'StatsSection',
  components: { AppIcon },
  data() {
    return {
      items: [],
    }
  },
  mounted() {
    this.items = stats.map((s) => ({ ...s, display: '0', started: false }))
    if (typeof IntersectionObserver === 'undefined') {
      this.items.forEach((it) => (it.display = String(it.value)))
      return
    }

    this.$nextTick(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const idx = Number(entry.target.dataset.idx)
            if (entry.isIntersecting && this.items[idx] && !this.items[idx].started) {
              this.items[idx].started = true
              this.runCount(this.items[idx])
              observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.2 }
      )

      const itemRefs = this.$el.querySelectorAll('[data-idx]')
      itemRefs.forEach((el) => el && observer.observe(el))
      this._observer = observer
    })
  },
  beforeDestroy() {
    if (this._observer) this._observer.disconnect()
  },
  methods: {
    runCount(item) {
      const target = item.value
      const duration = 1600
      const startTs = performance.now()
      const easeOutCubic = (p) => 1 - Math.pow(1 - p, 3)
      const step = (now) => {
        const progress = Math.min((now - startTs) / duration, 1)
        item.display = String(Math.round(target * easeOutCubic(progress)))
        if (progress < 1) requestAnimationFrame(step)
        else item.display = String(target)
      }
      requestAnimationFrame(step)
    },
  },
}
</script>

<template>
  <section class="section section--dark stats-section" id="stats">
    <div class="stats-section__mesh" aria-hidden="true">
      <span class="stats-section__mesh-blob stats-section__mesh-blob--1"></span>
      <span class="stats-section__mesh-blob stats-section__mesh-blob--2"></span>
    </div>
    <div class="container">
      <div class="section-head section-head--light reveal">
        <span class="section-eyebrow">数据与信任</span>
        <h2>可落地、有人服务的门店管理伙伴</h2>
        <p>每一组数字背后，都是客户的真实信任</p>
      </div>

      <div class="stats-grid">
        <div v-for="(it, i) in items" :key="i" class="stats-item reveal" :data-idx="i">
          <div class="stats-item__num">
            {{ it.display }}<span class="stats-item__suffix">{{ it.suffix }}</span>
          </div>
          <div class="stats-item__label">{{ it.label }}</div>
        </div>
      </div>

      <p class="stats-note">
        <AppIcon name="shield" :size="14" />
        数据持续更新，最终以官方发布为准
      </p>
    </div>
  </section>
</template>


<style lang="scss" scoped>
.stats-section {
  position: relative;
  overflow: hidden;

  &__mesh { position: absolute; inset: 0; pointer-events: none; }
  &__mesh-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(70px);
    &--1 { width: 460px; height: 460px; top: -140px; left: 28%; background: radial-gradient(circle, rgba(168, 85, 247, 0.4), transparent 65%); }
    &--2 { width: 380px; height: 380px; bottom: -120px; right: 14%; background: radial-gradient(circle, rgba(236, 72, 153, 0.32), transparent 62%); }
  }
}

.stats-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  max-width: 960px;
  margin: 0 auto;
}

.stats-item {
  text-align: center;
  padding: 36px 12px;
  // 固定卡片高度，避免内容长短不一导致参差
  min-height: 190px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  // 毛玻璃：深色 mesh 光斑之上的玻璃卡片
  background: rgba(255, 255, 255, 0.07);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  backdrop-filter: blur(16px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: $radius-lg;
  box-shadow: 0 10px 36px rgba(0, 0, 0, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.18);
  transition: border-color $motion-base $ease-out, background $motion-base $ease-out, transform $motion-base $ease-out;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-4px);
  }

  &__num {
    font-family: $font-family-num;
    font-size: 48px;
    font-weight: $weight-black;
    color: $color-white;
    line-height: 1;
    letter-spacing: -0.02em;
    white-space: nowrap; // 数字+后缀不换行，保证一致
  }

  &__suffix { color: $color-secondary-light; margin-left: 4px; font-size: 0.6em; font-weight: $weight-bold; }
  &__label { margin-top: 16px; color: rgba(255, 255, 255, 0.68); font-size: 14px; font-weight: $weight-medium; }
}

.stats-note {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 32px;
  color: rgba(255, 255, 255, 0.45);
  font-size: 13px;

  svg { color: $color-text-light; }
}

@media (max-width: $bp-md) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .stats-item { min-height: 150px; padding: 28px 12px; }
  .stats-item__num { font-size: 40px; }
}

@media (max-width: $bp-sm) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
  .stats-item { min-height: 130px; padding: 24px 8px; }
  .stats-item__num { font-size: 30px; }
}
</style>



// WEBPACK FOOTER //
// src/site/components/home/StatsSection.vue